import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const PHASE_LABEL = {
  skin_resilience: "Skin Resilience",
  making_actives_count: "Making Actives Count",
  precision_corrections: "Precision Corrections",
};
const SKIN_LABEL = { dry_sensitive: "Dry / Sensitive", normal_combo: "Normal / Combination", oily: "Oily" };
const VARIATION_LABEL = { none: "—", acne_prone: "Acne-prone", rosacea_prone: "Rosacea / redness-prone" };
const VERDICT_CLASS = { green: "verdict-green", yellow: "verdict-yellow", red: "verdict-red" };

export default function ProtocolSummary() {
  const { user } = useAuth();
  const [protocol, setProtocol] = useState(null);
  const [products, setProducts] = useState([]);
  const today = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  useEffect(() => {
    api.get("/protocol").then((r) => setProtocol(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  if (!protocol) {
    return <div className="max-w-4xl mx-auto px-8 py-12 aa-eyebrow">Loading…</div>;
  }

  return (
    <div className="summary-root" data-testid="page-summary">
      {/* Print controls — hidden on print */}
      <div className="no-print sticky top-0 bg-[var(--aa-bg)] border-b border-[var(--aa-elevated)] z-10 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <Link to="/dashboard" className="aa-eyebrow hover:text-[var(--aa-text)]" data-testid="summary-back">← Back to dashboard</Link>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="summary-print" onClick={() => window.print()} className="aa-cta-primary">Print / Save as PDF</button>
        </div>
      </div>

      <div className="summary-page max-w-3xl mx-auto px-10 py-12 print:py-6 print:max-w-none print:px-0">
        {/* Header */}
        <header className="border-b border-[var(--aa-elevated)] pb-6">
          <div className="aa-eyebrow">Ageless Alchemy Protocol Companion · My Protocol Summary</div>
          <h1 className="font-display text-4xl mt-2">For {user?.name || "—"}</h1>
          <div className="text-sm text-[var(--aa-text-3)] mt-1">Generated {today}</div>
        </header>

        {/* Profile */}
        <section className="mt-8 grid grid-cols-3 gap-6">
          <Field label="Current phase" value={PHASE_LABEL[protocol.phase]} />
          <Field label="Skin type" value={SKIN_LABEL[protocol.skin_type]} />
          <Field label="Tendency" value={VARIATION_LABEL[protocol.variation] || "—"} />
        </section>

        {/* AM Routine */}
        <section className="mt-10">
          <h2 className="font-display text-2xl border-b border-[var(--aa-elevated)] pb-2 mb-4">Morning</h2>
          <ol className="space-y-3">
            {protocol.am.map((s, i) => (
              <Row key={i} idx={i + 1} step={s} />
            ))}
          </ol>
        </section>

        {/* PM Routine */}
        <section className="mt-8">
          <h2 className="font-display text-2xl border-b border-[var(--aa-elevated)] pb-2 mb-4">Evening</h2>
          <ol className="space-y-3">
            {protocol.pm.map((s, i) => (
              <Row key={i} idx={i + 1} step={s} />
            ))}
          </ol>
        </section>

        {/* Purpose & Process */}
        {protocol.purpose_process && Object.keys(protocol.purpose_process).length > 0 && (
          <section className="mt-10 break-before-page">
            <h2 className="font-display text-2xl border-b border-[var(--aa-elevated)] pb-2 mb-4">Purpose & Process</h2>
            <div className="space-y-5">
              {Object.entries(protocol.purpose_process).map(([cat, pp]) => (
                <div key={cat}>
                  <div className="aa-eyebrow mb-1">{cat.replace("_", " ")}</div>
                  <div className="text-sm"><strong>Purpose:</strong> {pp.purpose}</div>
                  <div className="text-sm mt-1"><strong>Process:</strong> {pp.process}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Analyzed products */}
        {products.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-2xl border-b border-[var(--aa-elevated)] pb-2 mb-4">My Analyzed Products</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left aa-eyebrow border-b border-[var(--aa-elevated)]">
                  <th className="py-2 pr-3">Product</th>
                  <th className="py-2 pr-3">Category</th>
                  <th className="py-2 pr-3">Verdict</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b border-[var(--aa-elevated)]">
                    <td className="py-2 pr-3">{p.product_name}</td>
                    <td className="py-2 pr-3 text-[var(--aa-text-3)]">{p.category.replace("_", " ")}</td>
                    <td className="py-2 pr-3">
                      <span className={`aa-pill text-xs ${VERDICT_CLASS[p.verdict]}`}>{p.verdict.toUpperCase()}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Footer disclaimer */}
        <footer className="mt-12 pt-6 border-t border-[var(--aa-elevated)] text-xs text-[var(--aa-text-3)] leading-relaxed">
          <p>This summary is for educational and informational purposes only and is not medical advice. It is based on the Ageless Alchemy program developed by Dr. Rey Hamidi. Always consult a qualified healthcare provider before making changes to your skincare routine. Generated by the Ageless Alchemy Protocol Companion app.</p>
        </footer>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .summary-page { padding: 0.4in 0.6in !important; }
          a { color: inherit !important; text-decoration: none !important; }
        }
      `}</style>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="aa-eyebrow text-[10px]">{label}</div>
      <div className="font-display text-lg">{value}</div>
    </div>
  );
}

function Row({ idx, step }) {
  return (
    <li className="flex gap-3 items-start">
      <span className="aa-eyebrow w-7 mt-1">{String(idx).padStart(2, "0")}</span>
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm text-[var(--aa-text-3)]">{step.step}</div>
          {step.swapped && (
            <span className="aa-pill verdict-green text-xs px-2 py-0.5">My swap</span>
          )}
        </div>
        {step.url ? (
          <a href={step.url} target="_blank" rel="noopener noreferrer" className="text-[var(--aa-text)] hover:underline">{step.product}</a>
        ) : (
          <div className="text-[var(--aa-text)]">{step.product}</div>
        )}
        {step.swapped && (
          <div className="text-sm text-[var(--aa-text-3)] mt-0.5 italic leading-relaxed">Replaces Dr Rey's: {step.swap_original_product}</div>
        )}
        {step.alt && <div className="text-sm text-[var(--aa-text-2)] mt-0.5 leading-relaxed">{step.alt_label || "Alternative"}: {step.alt}</div>}
        {step.note && <div className="text-sm italic text-[var(--aa-text-3)] mt-0.5 leading-relaxed">{step.note}</div>}
      </div>
    </li>
  );
}
